import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
    useLayoutEffect,
} from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    linkWithCredential,
    OAuthProvider,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
} from 'firebase/auth';
import { auth, createMicrosoftProvider } from '@/shared/services/firebase';
import { userService } from '@/shared/services/userService';
import {
    authConfigService,
    DEFAULT_AUTH_CONFIG,
} from '@/shared/services/authConfigService';
import { clearDrafts, setDraftOwner } from '@/shared/utils/drafts';
import { isEmailVerified } from '@/shared/utils/emailVerified';
import { TEAM_EMAIL } from '@/shared/utils/contact';
import { setPreviewGuard } from '@/shared/services/previewGuard';
import {
    getPreviewableRoles,
    PREVIEW_ROLE_LABELS,
    readStoredPreview,
    writeStoredPreview,
} from '@/shared/utils/previewRoles';

const AuthContext = createContext();

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasProfile, setHasProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    // True while a password sign-in is being followed by a Microsoft link.
    // The sign-in flips the global auth state a full round trip before the
    // link resolves; AuthRestrictedRoutes reads this to keep the login page
    // mounted until the outcome is known, so a failed link still has a page
    // to report to.
    const [linking, setLinking] = useState(false);
    // Mirror of `linking` for the profile effect, which must not fetch while
    // a link is in flight (the link step checks the session itself once the
    // outcome is known) and must not re-run merely because the flag flipped.
    const linkingRef = useRef(false);
    // The environment's sign-in policy, from the server. Until it arrives the
    // defaults offer both methods; the server enforces the real policy on
    // every request regardless, so the worst case is a method shown that the
    // server then refuses with a message.
    const [authConfig, setAuthConfig] = useState(DEFAULT_AUTH_CONFIG);
    // A sentence for the login page when the server refused a sign-in the
    // client had accepted: which method or email domain is not allowed here.
    const [authNotice, setAuthNotice] = useState('');
    // A role the account has chosen to view the site as. Presentation only:
    // the server authorizes on the stored roles and never sees this.
    const [preview, setPreview] = useState(readStoredPreview);

    useEffect(() => {
        let cancelled = false;
        authConfigService
            .getAuthConfig()
            .then((remote) => {
                if (cancelled || !remote || !Array.isArray(remote.methods)) {
                    return;
                }
                setAuthConfig({
                    methods: remote.methods,
                    password: {
                        allowedDomains: Array.isArray(
                            remote.password?.allowedDomains
                        )
                            ? remote.password.allowedDomains
                            : DEFAULT_AUTH_CONFIG.password.allowedDomains,
                    },
                    microsoft: {
                        tenant:
                            remote.microsoft?.tenant ||
                            DEFAULT_AUTH_CONFIG.microsoft.tenant,
                    },
                });
            })
            .catch((error) => {
                console.error('Error fetching sign-in configuration:', error);
            });
        return () => {
            cancelled = true;
        };
    }, []);
    const profileFetchIdRef = useRef(0);
    // The Microsoft Graph access token from the last Microsoft sign-in. The
    // server reads the person's directory entry with it once, at profile
    // creation, so it is held in memory only and dropped when the session
    // ends or the profile exists. Never write it to web storage: it opens the
    // person's Microsoft account for its lifetime.
    const microsoftAccessTokenRef = useRef(null);

    // The account this provider holds a session for, so a direct change from
    // one account to another is told apart from an update to the same one.
    const uidRef = useRef(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            const uid = firebaseUser?.uid || null;
            // Another account signed in over this one with no signed-out
            // state between (a sign-out and sign-in in another tab can reach
            // this tab as one change). Nothing of the first account may reach
            // the second: its drafts go, a profile load still in flight for
            // it is dropped, and the profile is unknown until the new one
            // loads, which takes the guarded pages down so they mount afresh.
            if (uidRef.current && uid && uid !== uidRef.current) {
                clearDrafts();
                profileFetchIdRef.current += 1;
                setUserProfile(null);
                setHasProfile(null);
                setProfileLoading(true);
            }
            uidRef.current = uid;
            // Before anything renders for this user, so a draft is read only
            // by its writer
            setDraftOwner(uid);
            setCurrentUser(firebaseUser);
            setLoading(false);
            if (!firebaseUser) {
                clearDrafts();
                setUserProfile(null);
                setHasProfile(null);
                setPreview(null);
                writeStoredPreview(null);
                microsoftAccessTokenRef.current = null;
            }
        });
        return () => unsubscribe();
    }, []);

    // Loads the profile for a given user rather than the one in state, so a
    // step that changes what the same user object means (linking a provider
    // to an unverified password account makes it verified) can reload
    // without waiting for a state change that never comes.
    const loadProfile = useCallback(async (user) => {
        if (!isEmailVerified(user)) {
            setHasProfile(null);
            setUserProfile(null);
            setProfileLoading(false);
            return;
        }

        const fetchId = ++profileFetchIdRef.current;
        setProfileLoading(true);

        try {
            const profile = await userService.getProfile();
            if (fetchId !== profileFetchIdRef.current) return;
            setUserProfile(profile);
            setHasProfile(true);
            // A session the server accepts ends whatever a refusal said.
            setAuthNotice('');
            return { refused: false };
        } catch (error) {
            if (fetchId !== profileFetchIdRef.current) return;
            if (error.response && error.response.status === 404) {
                setHasProfile(false);
                setUserProfile(null);
            } else if (
                error.response &&
                error.response.status === 403 &&
                error.response.data?.code === 'EMAIL_NOT_VERIFIED'
            ) {
                // Only reachable when the client already treats the account
                // as verified, so the token most likely predates the
                // verification: the ID token the API sends is cached for up
                // to an hour. Refresh once and fetch again. If the server
                // still refuses, nothing this session can do will change
                // that, so it ends with a notice rather than leaving the
                // route guards waiting for a profile that never loads.
                let reloadOk = false;
                let retryError = null;
                try {
                    await user.reload();
                    reloadOk = true;
                    if (user.emailVerified) {
                        await user.getIdToken(true);
                        const profile = await userService.getProfile();
                        if (fetchId !== profileFetchIdRef.current) return;
                        setUserProfile(profile);
                        setHasProfile(true);
                        setAuthNotice('');
                        return { refused: false };
                    }
                } catch (err) {
                    retryError = err;
                }
                if (fetchId !== profileFetchIdRef.current) return;
                const retryStatus = retryError?.response?.status;
                if (retryStatus === 404) {
                    // Verified, and the API accepted the refreshed token;
                    // there is simply no profile yet.
                    setHasProfile(false);
                    setUserProfile(null);
                    return { refused: false };
                }
                const stillUnverified =
                    (reloadOk && !retryError && !user.emailVerified) ||
                    (retryStatus === 403 &&
                        retryError?.response?.data?.code ===
                            'EMAIL_NOT_VERIFIED');
                if (!stillUnverified) {
                    // A dropped connection or a server error during the retry
                    // says nothing about verification: leave the session
                    // alone, as the generic branch below does.
                    console.error(
                        'Error fetching user profile:',
                        retryError || error
                    );
                    setHasProfile(null);
                    setUserProfile(null);
                    return { refused: false };
                }
                setAuthNotice(
                    'Verify your email address from the message we sent, then sign in again.'
                );
                setHasProfile(null);
                setUserProfile(null);
                try {
                    await signOut(auth);
                } catch {
                    // The notice is what matters; a failed sign-out must not
                    // strand the page on a spinner.
                }
                setCurrentUser(null);
                return { refused: true };
            } else if (
                error.response &&
                error.response.status === 403 &&
                typeof error.response.data?.code === 'string' &&
                (error.response.data.code.startsWith('SIGN_IN_') ||
                    error.response.data.code === 'ACCOUNT_DEACTIVATED')
            ) {
                // The server refused this session: the sign-in policy does
                // not allow the method or email domain, or an admin has
                // deactivated the account. Nothing the person retries with
                // this session can change that, so end it and say why on the
                // login page.
                // The deactivation sentence is built here so it carries the
                // team's address, which the server's wording does not.
                setAuthNotice(
                    error.response.data.code === 'ACCOUNT_DEACTIVATED'
                        ? `This account has been deactivated. Contact the Peer Support Hub team at ${TEAM_EMAIL}.`
                        : error.response.data.message ||
                              'This sign-in method is not enabled here.'
                );
                setHasProfile(null);
                setUserProfile(null);
                try {
                    await signOut(auth);
                } catch {
                    // The notice is what matters; a failed sign-out must not
                    // strand the page on a spinner.
                }
                setCurrentUser(null);
                return { refused: true };
            } else {
                console.error('Error fetching user profile:', error);
                setHasProfile(null);
                setUserProfile(null);
            }
        } finally {
            if (fetchId === profileFetchIdRef.current) {
                setProfileLoading(false);
            }
        }
    }, []);

    const fetchProfile = useCallback(
        () => loadProfile(currentUser),
        [loadProfile, currentUser]
    );

    useEffect(() => {
        if (linkingRef.current) {
            return;
        }
        fetchProfile();
    }, [fetchProfile]);

    const signup = useCallback(async (email, password) => {
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
        );
        await sendEmailVerification(userCredential.user);
        return userCredential;
    }, []);

    const login = useCallback(async (email, password) => {
        const userCredential = await signInWithEmailAndPassword(
            auth,
            email,
            password
        );
        microsoftAccessTokenRef.current = null;
        // Force React state update — onAuthStateChanged may not re-fire on re-auth
        // with the same user, leaving stale emailVerified in state
        setCurrentUser(auth.currentUser);
        return userCredential;
    }, []);

    // Popup rather than redirect: the page keeps its state, and the result
    // arrives in the same session instead of on a reload the router has to
    // recover from. The provider's popup is a separate window, so the site's
    // frame policy does not apply to it.
    const loginWithMicrosoft = useCallback(async () => {
        let userCredential;
        try {
            userCredential = await signInWithPopup(
                auth,
                createMicrosoftProvider(authConfig.microsoft.tenant)
            );
        } catch (err) {
            // The address already belongs to a password account. Firebase
            // hands back the Microsoft credential it just obtained; keeping
            // it lets the login page link the two once the person proves the
            // password, instead of sending them away to start over.
            if (err?.code === 'auth/account-exists-with-different-credential') {
                const error = new Error(
                    'An account with this email already uses a password'
                );
                error.code = err.code;
                error.email = err.customData?.email;
                error.pendingCredential =
                    OAuthProvider.credentialFromError(err);
                throw error;
            }
            throw err;
        }
        // A Microsoft account is trusted as verified on the strength of the
        // provider, so the address it supplies is the whole identity. A
        // directory object with no mail attribute yields none, and letting
        // that account through would dead-end at profile creation.
        if (!userCredential.user?.email) {
            try {
                await signOut(auth);
            } catch (_signOutError) {
                // The person must see the missing-email message either way;
                // a failed sign-out is not a different problem to them.
            }
            const error = new Error(
                'Microsoft sign-in returned no email address'
            );
            error.code = 'auth/missing-email';
            throw error;
        }
        microsoftAccessTokenRef.current =
            OAuthProvider.credentialFromResult(userCredential)?.accessToken ||
            null;
        setCurrentUser(auth.currentUser);
        return userCredential;
    }, [authConfig]);

    const getMicrosoftAccessToken = useCallback(
        () => microsoftAccessTokenRef.current,
        []
    );

    const clearMicrosoftAccessToken = useCallback(() => {
        microsoftAccessTokenRef.current = null;
    }, []);

    // Proves ownership of the existing password account first; linking on an
    // email match alone would let a Microsoft account carrying a spoofed
    // address take over a profile. After this, both methods open one account.
    const linkMicrosoftToPasswordAccount = useCallback(
        async (email, password, pendingCredential) => {
            setLinking(true);
            linkingRef.current = true;
            try {
                const userCredential = await signInWithEmailAndPassword(
                    auth,
                    email,
                    password
                );
                try {
                    await linkWithCredential(
                        userCredential.user,
                        pendingCredential
                    );
                } catch (linkError) {
                    // The password was right but the link was refused. Leave
                    // the state as it was rather than half-done: signed out,
                    // with the reason shown on the login page. The stage tag
                    // lets the page tell an expired Microsoft credential
                    // apart from a wrong password, which share an error code.
                    try {
                        await signOut(auth);
                    } catch {
                        // The refused-link reason must reach the page either
                        // way; a failed sign-out is not what they need told.
                    }
                    linkError.linkStage = true;
                    throw linkError;
                }
                // The linked user is the same object React already holds, so
                // setting it again changes nothing; the profile is loaded for
                // it directly. An unverified password account becomes verified
                // through the new provider entry at this point.
                setCurrentUser(auth.currentUser);
                const outcome = await loadProfile(auth.currentUser);
                // In a Microsoft-only environment the link succeeded but this
                // session was opened with the password, and its token says
                // so; the server refuses it. The accounts are linked, which
                // is the news to give, and the next Microsoft sign-in works.
                if (outcome?.refused) {
                    setAuthNotice(
                        'Your accounts are linked. Sign in with your MSU Denver account.'
                    );
                    const error = new Error(
                        'Linked; a Microsoft sign-in is required here'
                    );
                    error.code = 'auth/linked-sign-in-required';
                    throw error;
                }
                return userCredential;
            } finally {
                linkingRef.current = false;
                setLinking(false);
            }
        },
        [loadProfile]
    );

    const clearAuthNotice = useCallback(() => setAuthNotice(''), []);

    const logout = useCallback(async () => {
        setPreview(null);
        writeStoredPreview(null);
        clearDrafts();
        setUserProfile(null);
        setHasProfile(null);
        setAuthNotice('');
        microsoftAccessTokenRef.current = null;
        return signOut(auth);
    }, []);

    const resetPassword = useCallback(
        (email) => sendPasswordResetEmail(auth, email),
        []
    );

    const reloadCurrentUser = useCallback(async () => {
        if (auth.currentUser) {
            await auth.currentUser.reload();
            // reload() refreshes the user record, not the ID token the API
            // sends; without a forced token refresh the server keeps seeing
            // email_verified: false for up to an hour after verification.
            await auth.currentUser.getIdToken(true);
            setCurrentUser(auth.currentUser);
            await loadProfile(auth.currentUser);
        }
    }, [loadProfile]);

    const refreshProfile = useCallback(() => {
        fetchProfile();
    }, [fetchProfile]);

    const realRoles = useMemo(() => userProfile?.roles || [], [userProfile]);
    const previewableRoles = useMemo(
        () => getPreviewableRoles(realRoles),
        [realRoles]
    );
    // A stored preview only counts for the account that set it and only while
    // that account still holds a role entitled to it.
    const previewRole =
        preview &&
        currentUser &&
        preview.uid === currentUser.uid &&
        previewableRoles.includes(preview.role)
            ? preview.role
            : null;
    const effectiveRoles = useMemo(
        () => (previewRole ? [previewRole] : realRoles),
        [previewRole, realRoles]
    );

    // Once the profile is known, a stored preview that does not belong to this
    // account (another person signed in) or is no longer allowed is dropped.
    useEffect(() => {
        if (preview && userProfile && !previewRole) {
            setPreview(null);
            writeStoredPreview(null);
        }
    }, [preview, userProfile, previewRole]);

    // Layout effect so the guard is in place before any child effect fires a
    // request in the same commit.
    useLayoutEffect(() => {
        setPreviewGuard(
            Boolean(previewRole),
            previewRole
                ? `You are viewing the site as ${PREVIEW_ROLE_LABELS[previewRole]}. Changes are turned off in this view. Select "Return to my view" to make changes.`
                : ''
        );
    }, [previewRole]);

    const startPreview = useCallback(
        (role) => {
            if (!currentUser || !previewableRoles.includes(role)) return;
            const next = { uid: currentUser.uid, role };
            setPreview(next);
            writeStoredPreview(next);
        },
        [currentUser, previewableRoles]
    );

    const stopPreview = useCallback(() => {
        setPreview(null);
        writeStoredPreview(null);
    }, []);

    const value = useMemo(
        () => ({
            currentUser,
            userProfile,
            realRoles,
            effectiveRoles,
            previewRole,
            previewableRoles,
            startPreview,
            stopPreview,
            loading,
            hasProfile,
            profileLoading,
            linking,
            authMethods: authConfig.methods,
            passwordAllowedDomains: authConfig.password.allowedDomains,
            authNotice,
            clearAuthNotice,
            signup,
            login,
            loginWithMicrosoft,
            getMicrosoftAccessToken,
            clearMicrosoftAccessToken,
            linkMicrosoftToPasswordAccount,
            logout,
            resetPassword,
            reloadCurrentUser,
            fetchProfile,
            refreshProfile,
        }),
        [
            currentUser,
            userProfile,
            realRoles,
            effectiveRoles,
            previewRole,
            previewableRoles,
            startPreview,
            stopPreview,
            loading,
            hasProfile,
            profileLoading,
            linking,
            authConfig,
            authNotice,
            clearAuthNotice,
            signup,
            login,
            loginWithMicrosoft,
            getMicrosoftAccessToken,
            clearMicrosoftAccessToken,
            linkMicrosoftToPasswordAccount,
            logout,
            resetPassword,
            reloadCurrentUser,
            fetchProfile,
            refreshProfile,
        ]
    );

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export default AuthContext;
